import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { ReceiversStateType } from './types';

import {
  ViewReceiverParams,
  AddReceiverParams,
  EditReceiverParams,
  DeleteReceiverParams,
  ReceiverLockedErrorType
} from '../../services/Receivers/types';

import {
  fetchViewReceiverList,
  fetchViewReceiver,
  fetchAddReceiver,
  fetchEditReceiver,
  fetchDeleteReceiver
} from '../../services';

const initialState: ReceiversStateType = {
  isReceiversLoading: false,
  receivers: []
}

export const viewReceiverList = createAsyncThunk(
  'viewReceiverList',
  async () => {
    return await fetchViewReceiverList()
  }
);

export const viewReceiver = createAsyncThunk(
  'viewReceiver',
  async (params: ViewReceiverParams) => {
    return await fetchViewReceiver(params)
  }
);

export const addReceiver = createAsyncThunk(
  'addReceiver',
  async (
    params: AddReceiverParams,
    { rejectWithValue }: { rejectWithValue: (value: ReceiverLockedErrorType) => any }
  ) => {
    try {
      return await fetchAddReceiver(params)
    } catch (error) {
      return rejectWithValue({
        msg: (error as ReceiverLockedErrorType).msg,
        status: (error as ReceiverLockedErrorType).status
      })
    }
  }
);

export const editReceiver = createAsyncThunk(
  'editReceiver',
  async (
    params: EditReceiverParams & { id: string },
    { rejectWithValue }: { rejectWithValue: (value: ReceiverLockedErrorType) => any }
  ) => {
    try {
      const { id, percentAmount, devicesOwner, remainingCommission } = params;
      return await fetchEditReceiver(id, { percentAmount, devicesOwner, remainingCommission })
    } catch (error) {
      return rejectWithValue({
        msg: (error as ReceiverLockedErrorType).msg,
        status: (error as ReceiverLockedErrorType).status
      })
    }
  }
);

export const deleteReceiver = createAsyncThunk(
  'deleteReceiver',
  async (
    params: DeleteReceiverParams,
    { rejectWithValue }: { rejectWithValue: (value: ReceiverLockedErrorType) => any }
  ) => {
    try {
      return await fetchDeleteReceiver(params)
    } catch (error) {
      return rejectWithValue({
        msg: (error as ReceiverLockedErrorType).msg,
        status: (error as ReceiverLockedErrorType).status
      })
    }
  }
);

export const receiversSlice = createSlice({
  name: 'receivers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(viewReceiverList.pending, state => {
      return {
        ...state,
        isReceiversLoading: true
      }
    });
    builder.addCase(viewReceiverList.fulfilled, (state, res) => {
      return {
        ...state,
        isReceiversLoading: false,
        receivers: res.payload.data.map(item => {
          return {
            ...item,
            devicesOwner: item.devicesOwner ? 1 : 0,
            remainingCommission: item.remainingCommission ? 1 : 0
          }
        })
      }
    });
    builder.addCase(viewReceiverList.rejected, state => {
      return {
        ...state,
        isReceiversLoading: false,
        receivers: []
      }
    });
    builder.addCase(addReceiver.pending, state => {
      return {
        ...state,
        isReceiversLoading: true
      }
    });
    builder.addCase(addReceiver.fulfilled, state => {
      return {
        ...state,
        isReceiversLoading: false
      }
    });
    builder.addCase(addReceiver.rejected, state => {
      return {
        ...state,
        isReceiversLoading: false
      }
    });
    builder.addCase(editReceiver.pending, state => {
      return {
        ...state,
        isReceiversLoading: true
      }
    });
    builder.addCase(editReceiver.fulfilled, state => {
      return {
        ...state,
        isReceiversLoading: false
      }
    });
    builder.addCase(editReceiver.rejected, state => {
      return {
        ...state,
        isReceiversLoading: false
      }
    });
    builder.addCase(deleteReceiver.pending, state => {
      return {
        ...state,
        isReceiversLoading: true
      }
    });
    builder.addCase(deleteReceiver.fulfilled, state => {
      return {
        ...state,
        isReceiversLoading: false
      }
    });
    builder.addCase(deleteReceiver.rejected, state => {
      return {
        ...state,
        isReceiversLoading: false
      }
    });
  }
});